import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  MenuIcon,
  SearchIcon,
  ShoppingCartIcon,
  HeartIcon,
  BellIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import ThemeToggle from "@/polymet/components/theme-toggle";
import AuthStatus from "@/polymet/components/auth-status";

// We no longer need category service as we've simplified the navigation

interface NavigationItem {
  href: string;
  label: string;
}

const navigationItems: NavigationItem[] = [];

interface SiteHeaderProps {
  onToggleMobileMenu: () => void;
  isScrolling: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export default function SiteHeader({
  onToggleMobileMenu,
  isScrolling,
  isMobile,
  isTablet,
  isDesktop
}: SiteHeaderProps) {
  return (
    <header 
      className={cn(
        "sticky top-0 z-40 w-full border-b border-border transition-all duration-200",
        isScrolling ? "bg-background/95 backdrop-blur-sm shadow-sm" : "bg-background/80 backdrop-blur-sm"
      )}
    >
      <div className="w-full">
        <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 md:px-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "mr-1 sm:mr-2",
                !isMobile ? "hidden" : "flex"
              )}
              onClick={onToggleMobileMenu}
              aria-label="Toggle menu"
            >
              <MenuIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>

            <Link to="/" className="flex items-center">
              <div className="font-bold text-lg sm:text-xl">
                <span className="text-black dark:text-white">Machine</span>
                <span className="text-blue-600">Search</span>
                <span className="text-black dark:text-white">.in</span>
              </div>
            </Link>
          </div>

          <div className="hidden md:flex items-center justify-center flex-1">
            <NavigationMenu>
              <NavigationMenuList className="gap-1 sm:gap-2">
                {navigationItems.map((item) => (
                  <NavigationMenuItem key={item.href}>
                    <NavigationMenuLink asChild>
                      <Link
                        to={item.href}
                        className="group inline-flex h-9 sm:h-10 w-max items-center justify-center rounded-md bg-background px-3 sm:px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
                      >
                        {item.label}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            <Button variant="ghost" size="icon" className="hidden sm:flex">
              <BellIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="sr-only">Notifications</span>
            </Button>

            <ThemeToggle />
            <AuthStatus />
          </div>
        </div>
      </div>
    </header>
  );
}
